import { Alarm, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Box, Button, Card, Grid, Tooltip, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { SearchContext } from '../../contexts/SearchContext';
import {
    ISearchResult
} from '../../interfaces';
import { AddToBasketDialog } from '../AddToBasketDialog';
import './index.scss';
import { ResultDetails } from './ResultDetails';
import { ResultMaterialsTable } from './ResultMaterialsTable';


export const SearchResult: React.SFC<ISearchResult> = ({
    materialGroup,
    disableAddToBasket = false,
    showHeaderLabels = true,
    ...props
}: ISearchResult) => {

    const searchContext = useContext(SearchContext);

    const [hover, setHover] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const [highlight, setHighlight] = useState(false);

    const isMaterialOut = [3, 4].includes(materialGroup.status ?? 0);
    const returnable = (materialGroup.status !== 4) && (materialGroup.totalAvailable ?? 0 > 0);

    const headerClasses = () => {

        let classString = "result-header-text";
        if (expanded) classString += " expanded";

        return classString;
    }
    const onExpand = () => {
        setExpanded(!expanded)
    }

    const showProperty = (propertyName: string) => {
        return searchContext.resultOptions
            .filter(x => x.propertyName === propertyName)[0]
            ?.visible;
    }
    useEffect(() => {
        let set = false;
        if (materialGroup.materials) {
            materialGroup.materials.forEach(x => {
                if (set === false && x.overdue === true) {
                    set = true;
                    if (highlight === false)
                        setHighlight(true);
                }
            });
        }

    }, [materialGroup]);


    const getQuantity = (category: string) => {

        const consumed = materialGroup.status === 4;

        switch (category) {
            case "DISPATCHED":
                return getGroupQuantity(category);
            case "CONSUMED":
                return getGroupQuantity(category);
            case "RETURNED":
                return getGroupQuantityReturned();
            case "AVAILABLE":
                return consumed ? 0 : getGroupQuantity();
            default:
                return materialGroup.totalAvailable;
        }
    }


    const getGroupQuantity = (category: string = "") => {

        //if results are filtered, the group may not
        //contain the same materials; total qty is dynamic

        var materials = cloneDeep(materialGroup.materials);

        if (materials && isMaterialOut) {
            //if any material qty is above 0 then discount any 0 qty
            //material in the group when looking at material out
            //so the historic entry does not skew dispatch/return qtys

            if (materials.some(x => (x.quantity ?? 0) > 0))
                materials = materials.filter(x => (x.quantity ?? 0) > 0);
        }

        switch (category) {
            case "DISPATCHED":
                return materials
                    ?.map(x => x.quantityDispatched ?? 0)
                    .reduce((sum, a) => sum + a, 0);
            case "CONSUMED":
                return materials
                    ?.map(x => x.quantityConsumed ?? 0)
                    .reduce((sum, a) => sum + a, 0);
            default:
                return materials
                    ?.map(x => x.quantity ?? 0)
                    .reduce((sum, a) => sum + a, 0);
        }

    }

    const getGroupQuantityReturned = () => {
        let qty = getGroupQuantity() ?? 0;
        let qtyD = getGroupQuantity("DISPATCHED") ?? 0;
        let qtyC = getGroupQuantity("CONSUMED") ?? 0;

        let qtyX = (qtyD - qty) - qtyC;

        return qtyX > 0 ? qtyX : 0;
    }

    const getGroupUnits = () => {
        return materialGroup.materials ? materialGroup.materials[0].unitOfQuantity.value : "";

    }


    return (

        <Grid item xs={12} >
            {materialGroup.materials && materialGroup.materials.length > 0 ?
                <Card
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    elevation={hover ? 3 : 1}
                    sx={{
                        p: showHeaderLabels ? 1 : .5,
                        mb: showHeaderLabels ? 1.5 : 0.5,
                        cursor: "pointer",
                        backgroundColor: returnable ? "#fff" : "#eee"
                    }}
                    onClick={onExpand}
                >
                    <Grid container>
                        <Grid item xs={isMaterialOut ? 4 : 6} container>
                            <Tooltip title={"Description Level 3: " + materialGroup.title ?? ""} >
                                <Grid item xs={4} className="result-header-item result-header-item-alt">
                                    {showHeaderLabels && <Typography className={headerClasses()}><strong>Description {isMaterialOut && <br />} Level 3</strong></Typography>}
                                    <Typography className={headerClasses()} >{materialGroup.title ?? ""}</Typography>
                                </Grid>
                            </Tooltip>

                            <Tooltip title={"Description Level 1: " + materialGroup.subtitle ?? ""} >
                                <Grid item xs={4} className="result-header-item">
                                    {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Level 1</strong></Typography>}
                                    <Typography className={headerClasses()} >{materialGroup.subtitle ?? ""}</Typography>
                                </Grid>
                            </Tooltip>

                            <Tooltip title={"Description Level 2: " + materialGroup.subtitle2 ?? ""} >
                                <Grid item xs={4} className="result-header-item">
                                    {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Level 2</strong></Typography>}
                                    <Typography className={headerClasses()} >{materialGroup.subtitle2 ?? ""}</Typography>
                                </Grid>
                            </Tooltip>
                        </Grid>

                        <Grid item xs={isMaterialOut ? 6 : 4} container>

                            {showProperty("vehicleClasses") &&
                                <Tooltip title={"Vehicle Classes: " + materialGroup.classes ?? ""} >
                                    <Grid item xs={isMaterialOut ? 1 : 3} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                        {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Class</strong></Typography>}
                                        <Typography className={headerClasses()} >{materialGroup.classes ?? ""}</Typography>
                                    </Grid>
                                </Tooltip>

                            }


                            {showProperty("ownedBy") &&
                                <Tooltip title={"Owned By: " + materialGroup.ownedBy} >
                                    <Grid item xs={isMaterialOut ? 1 : 3} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                        {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Owned By</strong></Typography>}
                                        <Typography className={headerClasses()} >{materialGroup.ownedBy}</Typography>
                                    </Grid>
                                </Tooltip>
                            }

                            {showProperty("spareType") &&
                                <Tooltip title={"Spare Type: " + materialGroup.spareType} >
                                    <Grid item xs={isMaterialOut ? 1 : 3} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                        {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Type</strong></Typography>}
                                        <Typography className={headerClasses()} >{materialGroup.spareType}</Typography>
                                    </Grid>
                                </Tooltip>
                            }



                            {showProperty("state") &&
                                <Tooltip title={"Material status: " + materialGroup.state} >
                                    <Grid item xs={isMaterialOut ? 1 : 2} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                        {showHeaderLabels && <Typography className={headerClasses()}><strong>{isMaterialOut && <br />}Status</strong></Typography>}
                                        <Typography className={headerClasses()} >{materialGroup.state}</Typography>
                                    </Grid>
                                </Tooltip>
                            }

                            {showProperty("quantity") && <>

                                {isMaterialOut &&
                                    <>
                                    <Tooltip title={`Dispatched quantity: ${getQuantity("DISPATCHED") } (${getGroupUnits()})`} >
                                            <Grid item xs={2} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                                {showHeaderLabels && <Typography className={headerClasses()}><strong>Total<br />  Dispatched</strong></Typography>}
                                                <Typography className={headerClasses()}> {getQuantity("DISPATCHED")}</Typography>
                                            </Grid>
                                        </Tooltip>
                                    <Tooltip title={`Consumed quantity: ${getQuantity("CONSUMED") } (${getGroupUnits()})`} >
                                            <Grid item xs={2} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                                {showHeaderLabels && <Typography className={headerClasses()}><strong>Total<br /> Consumed</strong></Typography>}
                                                <Typography className={headerClasses()}> {getQuantity("CONSUMED")}</Typography>
                                            </Grid>
                                        </Tooltip>
                                    <Tooltip title={`Returned quantity: ${getQuantity("RETURNED")} (${getGroupUnits()})`} >
                                            <Grid item xs={2} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                                {showHeaderLabels && <Typography className={headerClasses()}><strong>Total<br />  Returned</strong></Typography>}
                                                <Typography className={headerClasses()}> {getQuantity("RETURNED")}</Typography>
                                            </Grid>
                                        </Tooltip>
                                    </>
                                }
                                <Tooltip title={`${isMaterialOut ? "Outstanding" : "Available"} quantity: ${getQuantity("AVAILABLE") } (${getGroupUnits()})`} >
                                    <Grid item xs={isMaterialOut ? 2 : 1} className="result-header-item" display={{ xs: 'none', md: 'flex' }}>
                                        {showHeaderLabels && <Typography className={headerClasses()}><strong>Total{isMaterialOut && <><br />Outstanding</>}</strong></Typography>}
                                        <Typography className={headerClasses()}> {getQuantity("AVAILABLE")}</Typography>
                                    </Grid>
                                </Tooltip>

                            </>
                            }
                        </Grid>

                        <Grid item xs={2} container spacing={.5} alignSelf={expanded ? "start" : "center"} justifyContent="right">

                            {highlight === true && <Tooltip title="Material is overdue">
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mr: 1 }}>
                                    <Alarm color="error" fontSize="small" />
                                </Box>
                            </Tooltip>}

                            {disableAddToBasket === false &&
                                <Grid item >

                                    {materialGroup.status !== 4 ? (materialGroup.totalAvailable ?? 0) > 0 ?

                                        <AddToBasketDialog
                                            materialGroup={materialGroup}
                                            onSubmitMaterial={() => {/* do something to this result... */ }}
                                        />
                                        :
                                        <Button disabled variant="outlined" color="warning" >Returned</Button>
                                        :
                                        <Button disabled variant="outlined">Consumed</Button>
                                    }


                                </Grid>
                            }
                            <Grid item >
                                <Tooltip title={(expanded ? "Hide" : "Show") + " additional details"}>
                                    <Button
                                        variant="outlined"
                                        onClick={onExpand}
                                        style={{ paddingLeft: 5, paddingRight: 5, minWidth: 10 }}
                                    >
                                        {expanded ? <ArrowDropUp /> : <ArrowDropDown />}
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>

                    <AnimateHeight height={expanded ? "auto" : 0} duration={500}>

                        <Grid container item sx={{ p: 1 }} spacing={.5} >

                            <ResultDetails
                                materialGroup={materialGroup}
                            />

                            <ResultMaterialsTable
                                isMaterialOut={isMaterialOut}
                                materialGroup={materialGroup}
                            />

                        </Grid>

                    </AnimateHeight>
                </Card>
                :
                <Typography color="error">No materials</Typography>
            }
        </Grid >

    );
}